import { useState, useCallback } from 'react';
import { CartItem, isValidCartItem } from '@/Context/CartContext';
import { cartLabels } from '@/data/content';

const CART_KEY = 'cartItems';

const loadCart = (): CartItem[] => {
    if (typeof window === 'undefined') return [];

    try {
        const raw = window.localStorage.getItem(CART_KEY);
        if (!raw) return [];
        const parsed: unknown = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter(isValidCartItem);
    } catch {
        return [];
    }
};

const persist = (items: CartItem[]) => {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(CART_KEY, JSON.stringify(items));
    } catch {
        // silently fail
    }
};

const optionsEqual = (a: Record<string, unknown>, b: Record<string, unknown>): boolean => {
    const keysA = Object.keys(a).sort();
    const keysB = Object.keys(b).sort();
    if (keysA.length !== keysB.length) return false;
    return keysA.every((k) => keysB.includes(k) && a[k] === b[k]);
};

type CartProductInput = {
    id: number;
    price: number;
    name?: string;
    date?: string;
    period?: string;
    fulfilment?: string;
    weight_percentage?: number;
    [key: string]: unknown;
};

interface UseCartStorageReturn {
    cartItems: CartItem[];
    addItem: (product: CartProductInput, quantity?: number, options?: Record<string, unknown>) => void;
    updateItem: (index: number, newQuantity: number) => void;
    removeItem: (index: number) => void;
    clearItems: () => void;
}

function useCartStorage(): UseCartStorageReturn {
    const [cartItems, setCartItems] = useState<CartItem[]>(loadCart);

    const addItem = useCallback((product: CartProductInput, quantity = 1, options: Record<string, unknown> = {}) => {
        const safeQty = Math.max(1, Math.min(99, Math.floor(Number(quantity) || 1)));

        setCartItems((prev) => {
            const existingIndex = prev.findIndex(
                (item) =>
                    item.id === product.id &&
                    optionsEqual(item.options ?? {}, options) &&
                    (item.date ?? null) === (product.date ?? null) &&
                    (item.menu_id ?? null) === (product.menu_id ?? null) &&
                    (item.menu_instance_id ?? null) === (product.menu_instance_id ?? null)
            );

            let next: CartItem[];

            if (existingIndex > -1) {
                next = [...prev];
                const existing = next[existingIndex];
                if (existing) {
                    next[existingIndex] = {
                        ...existing,
                        quantity: Math.min(99, existing.quantity + safeQty),
                    };
                }
            } else {
                next = [
                    ...prev,
                    {
                        ...product,
                        quantity: safeQty,
                        options,
                        name: String(product.name || cartLabels.product_fallback_label),
                    } as CartItem,
                ];
            }

            persist(next);
            return next;
        });
    }, []);

    const updateItem = useCallback((index: number, newQuantity: number) => {
        setCartItems((prev) => {
            if (newQuantity < 1) {
                const next = prev.filter((_, i) => i !== index);
                persist(next);
                return next;
            }

            const safeQty = Math.min(99, Math.floor(Number(newQuantity) || 1));
            const next = [...prev];
            const target = next[index];
            if (target) {
                next[index] = { ...target, quantity: safeQty };
            }
            persist(next);
            return next;
        });
    }, []);

    const removeItem = useCallback((index: number) => {
        setCartItems((prev) => {
            const next = prev.filter((_, i) => i !== index);
            persist(next);
            return next;
        });
    }, []);

    const clearItems = useCallback(() => {
        setCartItems([]);
        persist([]);
    }, []);

    return { cartItems, addItem, updateItem, removeItem, clearItems };
}

export default useCartStorage;
